/**
 * Hardcoded sample story for the no-account demo at `/try`.
 *
 * Nothing here ever touches the database. It hydrates the demo Zustand store on
 * first load so the editor looks alive and the AI assistant has real context to
 * read (chapters + a small world wiki).
 */

export type DemoLoreCategory =
    | "characters"
    | "locations"
    | "systems"
    | "factions"
    | "organizations"
    | "other";

export type DemoLoreSeed = {
    id: string;
    name: string;
    category: DemoLoreCategory;
    categoryLabel: string;
    description: string;
    categoryId: string;
    parentEntryId: string | null;
    content: Record<string, unknown> | null;
    imageUrl: string | null;
};

export type DemoLoreCategorySeed = {
    id: string;
    category: DemoLoreCategory;
    label: string;
};

export type DemoLoreConnectionSeed = {
    id: string;
    sourceEntryId: string;
    targetEntryId: string;
    relationLabel: string;
};

export type DemoChapterSeed = {
    id: string;
    title: string;
    sortOrder: number;
    status: "draft" | "published";
    wordCount: number;
    /** Tiptap JSON document. */
    content: Record<string, unknown>;
    /** End-of-chapter author's note (Tiptap JSON), if the author added one. */
    authorNote?: Record<string, unknown> | null;
};

export type DemoStorySeed = {
    id: string;
    title: string;
    tagline: string;
};

// ─── Tiptap doc helpers ──────────────────────────────────

function p(text: string) {
    return { type: "paragraph", content: [{ type: "text", text }] };
}
function h(level: number, text: string) {
    return {
        type: "heading",
        attrs: { level },
        content: [{ type: "text", text }],
    };
}
function doc(...nodes: object[]) {
    return { type: "doc", content: nodes };
}
function countWords(nodes: { content?: { text?: string }[] }[]): number {
    return nodes
        .flatMap((n) => (n.content ?? []).map((c) => c.text ?? ""))
        .join(" ")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
}

// ─── The sample story ────────────────────────────────────

export const DEMO_STORY: DemoStorySeed = {
    id: "demo-story",
    title: "The Lantern Below",
    tagline: "A drowned-city web fiction sandbox",
};

const ch1Nodes = [
    h(1, "Chapter 1 — The Tide Bell"),
    p(
        "The bell beneath Aldermere only rang when someone was about to drown, and tonight it would not stop.",
    ),
    p(
        "Sefi pressed her palm to the cold brass of the listening-pipe and counted the strikes. Seven. Always seven, her grandmother had said, was the city asking to be remembered. She had never believed it until the water in her cupped hands began, very softly, to hum.",
    ),
    p(
        "Above her, the lamps of the Drowned Quarter swayed on their chains, throwing green light across the wet stone. Somewhere out past the breakwater, the old cartographer Brannoc was waiting with a map he swore would lead them under the seawall — to the lantern that had kept the city dry for three hundred years, and had, last week, gone dark.",
    ),
    p(
        '"You\'re late," she told the empty stair, because saying it to no one was easier than admitting she was afraid to climb down.',
    ),
];

const ch2Nodes = [
    h(1, "Chapter 2 — Brannoc's Map"),
    p(
        "Brannoc unrolled the chart across the upturned hull, and the ink moved.",
    ),
    p(
        "Not metaphorically — the lines slid like silt finding a new channel, redrawing the alleys of Aldermere as they had been before the flood. Sefi had seen tide-ink before, but never a whole city's worth, and never one that seemed to flinch when she looked too closely at the blank district at its heart.",
    ),
    p(
        '"That\'s where the lantern is," Brannoc said, tapping the emptiness. "And that\'s where the map stops telling the truth. Past the seawall, you trust your hands, not your eyes."',
    ),
    p(
        "Sefi thought of the humming water and said nothing. The bell, far below, struck its seventh note and fell silent for the first time in a day.",
    ),
];

export const DEMO_CHAPTERS: DemoChapterSeed[] = [
    {
        id: "demo-ch-1",
        title: "The Tide Bell",
        sortOrder: 1,
        status: "published",
        wordCount: countWords(ch1Nodes),
        content: doc(...ch1Nodes),
    },
    {
        id: "demo-ch-2",
        title: "Brannoc's Map",
        sortOrder: 2,
        status: "draft",
        wordCount: countWords(ch2Nodes),
        content: doc(...ch2Nodes),
    },
];

export const DEMO_LORE_CATEGORIES: DemoLoreCategorySeed[] = [
    {
        id: "demo-category-characters",
        category: "characters",
        label: "Characters",
    },
    {
        id: "demo-category-locations",
        category: "locations",
        label: "Locations",
    },
    {
        id: "demo-category-systems",
        category: "systems",
        label: "Systems",
    },
];

function lore(
    id: string,
    name: string,
    category: DemoLoreCategory,
    categoryId: string,
    categoryLabel: string,
    description: string,
): DemoLoreSeed {
    return {
        id,
        name,
        category,
        categoryId,
        categoryLabel,
        description,
        parentEntryId: null,
        content: doc(p(description)),
        imageUrl: null,
    };
}

export const DEMO_LORE: DemoLoreSeed[] = [
    lore(
        "demo-lore-sefi",
        "Sefi",
        "characters",
        "demo-category-characters",
        "Characters",
        "A young tide-listener of the Drowned Quarter; can hear the city's bell in cupped water.",
    ),
    lore(
        "demo-lore-brannoc",
        "Brannoc",
        "characters",
        "demo-category-characters",
        "Characters",
        "An aging cartographer who draws with tide-ink and keeps the only map of the flooded undercity.",
    ),
    lore(
        "demo-lore-aldermere",
        "Aldermere",
        "locations",
        "demo-category-locations",
        "Locations",
        "A half-sunken harbour city kept dry for three centuries by a great lantern beneath the seawall.",
    ),
    lore(
        "demo-lore-quarter",
        "The Drowned Quarter",
        "locations",
        "demo-category-locations",
        "Locations",
        "The lowest district of Aldermere, lit by green lamps on chains and threaded with listening-pipes.",
    ),
    lore(
        "demo-lore-lantern",
        "The Lantern",
        "systems",
        "demo-category-systems",
        "Systems",
        "An ancient light beneath the seawall that holds back the sea. It went dark a week ago.",
    ),
];

export const DEMO_LORE_CONNECTIONS: DemoLoreConnectionSeed[] = [
    {
        id: "demo-connection-sefi-aldermere",
        sourceEntryId: "demo-lore-sefi",
        targetEntryId: "demo-lore-aldermere",
        relationLabel: "lives in",
    },
];
